import React, { useMemo, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Container from '@cloudscape-design/components/container';
import CopyToClipboard from '@cloudscape-design/components/copy-to-clipboard';
import Header from '@cloudscape-design/components/header';
import Pagination from '@cloudscape-design/components/pagination';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table, { TableProps } from '@cloudscape-design/components/table';
import TextFilter from '@cloudscape-design/components/text-filter';
import { useCollection } from '@cloudscape-design/collection-hooks';
import { CellValue, JsonObject, cellText, columnLabel, toCellValue, toTableModel } from './resultFormat';

/**
 * A page-defined column: `value` drives the cell text, filtering and sorting.
 * `cell` replaces the rendered content (e.g. a row action); `sortable: false` turns sorting off.
 */
export interface ResultColumn<T> {
  id: string;
  header: string;
  value: (row: T) => string | null | undefined;
  cell?: (row: T) => React.ReactNode;
  sortable?: boolean;
}

interface ResultViewerProps {
  data: unknown;
  error?: string;
  /**
   * Page-specific rows and columns for the Table view. When both are set they replace the
   * generic table; the JSON view and Copy JSON still use the raw `data`.
   */
  rows?: object[] | null;
  columns?: ResultColumn<any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
  /** Heading above the table. Defaults to "Rows". */
  title?: string;
}

type ViewMode = 'table' | 'json';

const PAGE_SIZE = 25;

const Cell: React.FC<{ value: CellValue }> = ({ value }) => {
  switch (value.kind) {
    case 'empty':
      return <Box color="text-status-inactive">-</Box>;
    case 'text':
      return <span>{value.text}</span>;
    case 'principal':
      return (
        <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
          <Badge color="blue">{value.type}</Badge>
          <span>{value.value}</span>
        </SpaceBetween>
      );
    case 'badges':
      return (
        <SpaceBetween direction="horizontal" size="xxs">
          {value.items.map((item, i) => (
            <Badge key={`${item}-${i}`}>{item}</Badge>
          ))}
        </SpaceBetween>
      );
    case 'paths':
      return (
        <SpaceBetween size="xxxs">
          {value.items.map((item, i) => (
            <Box key={`${item}-${i}`} variant="code">
              {item}
            </Box>
          ))}
        </SpaceBetween>
      );
  }
};

/** Columns for a response the page has no columns for: one per key, rendered by `Cell`. */
const genericColumns = (keys: string[]): ResultColumn<JsonObject>[] =>
  keys.map(c => ({
    id: c,
    header: columnLabel(c),
    value: row => cellText(row[c]),
    cell: row => <Cell value={toCellValue(row[c])} />,
  }));

function CustomTable<T extends object>({ columns, rows, title }: { columns: ResultColumn<T>[]; rows: T[]; title: string }) {
  const text = (row: T, c: ResultColumn<T>) => c.value(row) ?? '';
  const { items, collectionProps, filterProps, paginationProps, filteredItemsCount } = useCollection(rows, {
    filtering: {
      filteringFunction: (row, filter) => {
        const needle = filter.trim().toLowerCase();
        return !needle || columns.some(c => text(row, c).toLowerCase().includes(needle));
      },
      empty: <Box textAlign="center">No rows returned</Box>,
      noMatch: <Box textAlign="center">No rows match the filter</Box>,
    },
    sorting: {},
    pagination: { pageSize: PAGE_SIZE },
  });

  const columnDefinitions: TableProps.ColumnDefinition<T>[] = columns.map(c => ({
    id: c.id,
    header: c.header,
    cell: row => {
      if (c.cell) return c.cell(row);
      const v = c.value(row);
      return v ? v : <Box color="text-status-inactive">-</Box>;
    },
    ...(c.sortable === false
      ? {}
      : { sortingComparator: (a: T, b: T) => text(a, c).localeCompare(text(b, c), undefined, { numeric: true }) }),
  }));

  const count = filterProps.filteringText ? `(${filteredItemsCount}/${rows.length})` : `(${rows.length})`;

  return (
    <Table
      {...collectionProps}
      items={items}
      columnDefinitions={columnDefinitions}
      variant="embedded"
      wrapLines
      header={<Header counter={count}>{title}</Header>}
      filter={<TextFilter {...filterProps} filteringPlaceholder="Filter rows" countText={`${filteredItemsCount} matches`} />}
      pagination={rows.length > PAGE_SIZE ? <Pagination {...paginationProps} /> : undefined}
      ariaLabels={{ tableLabel: 'Response rows' }}
    />
  );
}

/** Shows an API response as a filterable table, with a raw JSON view and a copy button. */
const ResultViewerInner: React.FC<ResultViewerProps> = ({ data, error, rows, columns, title = 'Rows' }) => {
  const [mode, setMode] = useState<ViewMode>('table');
  const json = useMemo(() => JSON.stringify(data, null, 2), [data]);
  const model = useMemo(() => toTableModel(data), [data]);

  if (error) {
    return (
      <Alert type="error" header="Request failed">
        {error}
      </Alert>
    );
  }
  if (data === undefined || data === null) return null;

  return (
    <Container
      header={
        <Header
          variant="h2"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <SegmentedControl
                selectedId={mode}
                onChange={({ detail }) => setMode(detail.selectedId as ViewMode)}
                label="Response view"
                options={[
                  { id: 'table', text: 'Table' },
                  { id: 'json', text: 'JSON' },
                ]}
              />
              <CopyToClipboard
                variant="button"
                copyButtonText="Copy JSON"
                copySuccessText="JSON copied"
                copyErrorText="Could not copy JSON"
                textToCopy={json}
              />
            </SpaceBetween>
          }
        />
      }
    >
      {mode === 'json' ? (
        <Box variant="code">
          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{json}</pre>
        </Box>
      ) : rows && columns ? (
        // No key: a reload after create/delete must keep the filter, sort and page (pagination clamps itself).
        <CustomTable columns={columns} rows={rows} title={title} />
      ) : model ? (
        // Keyed on the JSON: a different response can have different columns.
        <CustomTable key={json} columns={genericColumns(model.columns)} rows={model.rows} title={title} />
      ) : (
        <Box color="text-status-inactive">The request succeeded and returned no content.</Box>
      )}
    </Container>
  );
};

/** Memoized so typing in the page form does not re-render a large result table. */
export const ResultViewer = React.memo(ResultViewerInner);
